import _ from 'lodash'

import {
  getMarkets,
  getEventGroups,
  getDeals,
  getVenues,
  getTickets,
  getGroups,
  getPerformance,
} from 'selectors/entities'
import {
  createDeepEqualSelector,
} from 'utils/selectors'
import { CALLOVER_LIST_VIEW_BY_GROUP } from 'constants/performance-page-constants'

export const getDraftDeals = state => state.draftDeals.deals

export const getChangedDraftDeals = state => {
  const updateableFields = [
    'ticketType',
    'discountPrice',
    'ticketCount',
    'isOnsale',
  ]

  const deals = getDeals(state)
  const draftDeals = getDraftDeals(state)

  return _.chain(draftDeals)
    .map(draftDeal => {
      const changedFields = { id: draftDeal.id }
      updateableFields.forEach(fieldName => {
        if (draftDeal[fieldName] !== deals[draftDeal.id][fieldName]) {
          changedFields[fieldName] = draftDeal[fieldName]
        }
      })
      return changedFields
    })
    .filter(draftDeal => Object.keys(draftDeal).length > 1)
    .value()
}

export const isEditable = state => !!getDraftDeals(state)

const getActiveDeals = state => (isEditable(state) ? getDraftDeals(state) : getDeals(state))

const getPerformanceEventGroup = _.memoize(performanceId => createDeepEqualSelector(
  [getPerformance(performanceId), getEventGroups],
  (performance, eventGroups) => (performance.eventGroup ? eventGroups[performance.eventGroup] : null)
))

export const getPerformanceMarket = _.memoize(performanceId => createDeepEqualSelector(
  [getPerformanceEventGroup(performanceId), getMarkets],
  (eventGroup, markets) => (eventGroup ? markets[eventGroup.market] : null)
))

export const dealsToGroupedDeals = (performance, deals) => {
  const performanceDeals = performance.deals.map(id => deals[id])
  const masters = performanceDeals.filter(deal => deal.isInventoryMaster)

  const groupedDeals = masters.map(master => {
    const slaves = performanceDeals.filter(deal => !deal.isInventoryMaster && deal.inventoryMaster === master.id)
    const sharedDeals = [master].concat(slaves)
    return {
      sharedAllocation: true,
      soldTickets: sharedDeals.reduce((prev, curr) => prev + curr.soldTickets, 0),
      ticketCount: master.ticketCount,
      totalPartnerShare: sharedDeals.reduce((prev, curr) => prev + Number(curr.totalPartnerShare), 0),
      id: master.id,
      masterId: master.id, // LEGACY - we can remove this when we ship the new perf page
      isOnsale: sharedDeals.some(curr => curr.isOnsale),
      sharedDeals,
    }
  })

  return groupedDeals.concat(performanceDeals.filter(deal => !deal.isInventoryMaster && !deal.inventoryMaster))
}

export const getUngroupedPerformanceDeals = _.memoize(performanceId => createDeepEqualSelector(
  [getPerformance(performanceId), getActiveDeals],
  (performance, deals) => (performance && deals && performance.deals
    ? _.keyBy(performance.deals.map(id => deals[id]), 'id')
    : null
  )
))

const getPerformanceDeals = _.memoize(performanceId => createDeepEqualSelector(
  [getPerformance(performanceId), getActiveDeals],
  (performance, deals) => (performance.deals ? dealsToGroupedDeals(performance, deals) : null)
))

const getPerformanceVenue = _.memoize(performanceId => createDeepEqualSelector(
  [getPerformanceEventGroup(performanceId), getVenues],
  (performanceEventGroup, venues) => (performanceEventGroup ? venues[performanceEventGroup.venue] : null)
))

const getPerformanceTickets = _.memoize(performanceId => createDeepEqualSelector(
  [getPerformance(performanceId), getTickets],
  (performance, tickets) => (performance.tickets ? performance.tickets.map(id => tickets[id]) : null)
))

export const getPerformanceInfo = _.memoize(performanceId => createDeepEqualSelector(
  [
    getPerformance(performanceId),
    getPerformanceEventGroup(performanceId),
    getPerformanceDeals(performanceId),
    getPerformanceVenue(performanceId),
    getPerformanceTickets(performanceId),
  ],
  (performance, eventGroup, deals, venue, tickets) => ({
    ...performance,
    eventGroup,
    deals,
    venue,
    tickets,
    soldTickets: _.sumBy(deals, 'soldTickets'),
    ticketCount: _.sumBy(deals, 'ticketCount'),
  })
))

const getOrigCustomerName = (ticket) => {
  if (ticket.origFirstName && ticket.origLastName) {
    return `${ticket.origLastName}, ${ticket.origFirstName}`
  }
  return null
}

const getAllPerformanceCalloverListGroups = _.memoize(performanceId => createDeepEqualSelector(
  [getPerformanceTickets(performanceId), getActiveDeals, getGroups],
  (rawTickets, deals, groups) => {
    if (!rawTickets) {
      return []
    }

    return _.chain(rawTickets)
      .groupBy(ticket => `${ticket.effectiveHolder}-${ticket.firstName}-${ticket.lastName}-${ticket.deal}`)
      .map(tickets => ({
        customerName: `${tickets[0].lastName}, ${tickets[0].firstName}`,
        origCustomerName: getOrigCustomerName(tickets[0]),
        email: tickets[0].email,
        phoneNumber: tickets[0].phoneNumber,
        ticketType: deals[tickets[0].deal].ticketType,
        ticketCount: tickets.length,
        groupId: tickets[0].group,
      }))
      .groupBy('groupId')
      .map((groupTickets, groupId) => ({
        groupName: groups[groupId].name,
        tickets: _.orderBy(groupTickets, ['customerName', 'ticketType'], ['asc', 'asc']),
      }))
      .value()
  }
))

export const getCalloverListViewType = (state) => state.pages.performancePage.calloverListViewType

export const getPerformanceCalloverList = _.memoize(performanceId => createDeepEqualSelector(
  [getCalloverListViewType, getAllPerformanceCalloverListGroups(performanceId)],
  (calloverListViewType, ticketGroups) => {
    let groupedTickets
    let ungroupedTickets

    switch (calloverListViewType) {
      case CALLOVER_LIST_VIEW_BY_GROUP:
        [groupedTickets, ungroupedTickets] = _.partition(ticketGroups, (group) => group.tickets.length > 1)
        break
      default:
        [groupedTickets, ungroupedTickets] = [[], ticketGroups]
        break
    }

    const sortedGroupedTickets = _.orderBy(groupedTickets, ['partyName'], ['asc'])
    const sortedUngroupedTickets = _.chain(ungroupedTickets)
      .reduce((flattened, current) => flattened.concat(current.tickets), [])
      .orderBy(['customerName', 'ticketType'], ['asc', 'asc'])
      .value()

    return {
      groupedTickets: sortedGroupedTickets,
      ungroupedTickets: sortedUngroupedTickets,
    }
  }
))

export const getAttendeeList = _.memoize(performanceId => createDeepEqualSelector(
  [getPerformanceTickets(performanceId), getActiveDeals, getGroups],
  (rawTickets, deals) => {
    if (!rawTickets) {
      return []
    }

    return _.chain(rawTickets)
      .groupBy(ticket => `${ticket.effectiveHolder}-${ticket.firstName}-${ticket.lastName}`)
      .map(tickets => {
        const ticketMeta = _.chain(tickets)
          .map(ticket => ({ ...ticket, deal: deals[ticket.deal].ticketType }))
          .reduce((prev, next) => ({ ...prev, [next.deal]: prev[next.deal] ? prev[next.deal] + 1 : 1 }), {})
          .map((val, key) => ({ ticketCount: val, ticketType: key }))
          .value()

        return {
          name: `${tickets[0].firstName} ${tickets[0].lastName}`,
          ticketCount: ticketMeta.map(({ ticketCount }) => ticketCount),
          ticketType: ticketMeta.map(({ ticketType }) => ticketType),
          phoneNumber: tickets[0].phoneNumber,
          email: tickets[0].email,
        }
      })
      .orderBy('name', 'asc')
      .value()
  }
))

export const isSaving = state => state.pages.performancePage.isSaving

export const getDealsErrors = state =>
  state.error && state.error.detail && state.error.detail.fieldMessages && state.error.detail.fieldMessages.deals



/** WEBPACK FOOTER **
 ** ./src/selectors/performance/performance-selectors.js
 **/